import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import {
  type Identifier,
  useGetList,
  useGetOne,
  useNotify,
  useUpdate,
} from "ra-core";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { InlineAvatar } from "../misc/InlineAvatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { Sale, Task } from "../types";

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

const initials = (sale?: Sale) =>
  sale
    ? `${sale.first_name?.[0] ?? ""}${sale.last_name?.[0] ?? ""}`.toUpperCase()
    : "?";

const SaleAvatar = ({ sale }: { sale?: Sale }) => (
  <InlineAvatar src={sale?.avatar?.src} fallback={initials(sale)} />
);

// Assignee (sales rep) avatar → click to reassign inline. Reassigning off the
// current user drops the task from their "my tasks" view (list invalidation).
export function AssigneePill({ task }: { task: Task }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [pinnedId, setPinnedId] = useState<Identifier | null>(null);
  // Show the just-picked sale immediately so the avatar doesn't flash the "?"
  // fallback while the getOne for the new id refetches.
  const [optimistic, setOptimistic] = useState<Sale | null>(null);

  const { data: current } = useGetOne<Sale>(
    "sales",
    { id: task.sales_id! },
    { enabled: task.sales_id != null },
  );
  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "last_name", order: "ASC" },
      filter: { "disabled@neq": true },
    },
    { enabled: open },
  );

  const reassign = (sale: Sale) => {
    setOptimistic(sale);
    update(
      "tasks",
      { id: task.id, data: { sales_id: sale.id }, previousData: task },
      {
        mutationMode: "optimistic",
        // No manual invalidate — realtime (`useRealtimeResource("tasks")`) is the
        // single reconciler. A second refetch here just races it and can echo
        // the pre-write value, which is what caused the avatar to bounce.
        onSuccess: () => notify("resources.tasks.updated", { type: "info" }),
        onError: () => setOptimistic(null),
      },
    );
  };

  // Show the just-picked sale and HOLD it for the entire time the picker is
  // open (the whole reassign interaction), so the refetch churn underneath can
  // never bounce the avatar. Release only once the picker is closed AND the
  // record has settled onto the pick.
  const shown = optimistic ?? current;
  useEffect(() => {
    if (optimistic && !open && current?.id === optimistic.id) {
      setOptimistic(null);
    }
  }, [optimistic, open, current]);

  // Pin the assignee that was current when the picker opened to the top, so the
  // order stays stable while open (reassigning doesn't make rows jump).
  const orderedSales = sales
    ? [
        ...sales.filter((sale) => sale.id === pinnedId),
        ...sales.filter((sale) => sale.id !== pinnedId),
      ]
    : [];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setPinnedId(task.sales_id ?? null);
      }}
    >
      <PopoverTrigger asChild onClick={stop}>
        <button
          type="button"
          className="rounded-full hover:ring-2 hover:ring-ring cursor-pointer"
          title={shown ? `${shown.first_name} ${shown.last_name}` : "Assign"}
        >
          <SaleAvatar sale={shown} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-0" onClick={stop}>
        <Command>
          <CommandInput placeholder="Assign to…" />
          <CommandList>
            <CommandEmpty>No one found.</CommandEmpty>
            <CommandGroup>
              {orderedSales.map((sale) => (
                <CommandItem
                  key={sale.id}
                  value={`${sale.first_name} ${sale.last_name}`}
                  onSelect={() => reassign(sale)}
                  className="cursor-pointer"
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      sale.id === task.sales_id ? "" : "invisible"
                    }`}
                  />
                  <SaleAvatar sale={sale} />
                  <span className="ml-2">
                    {sale.first_name} {sale.last_name}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
